import React, { useState, useEffect, useRef } from 'react';

// Pixel-perfect SVG sparkline — uses ResizeObserver so text is never stretched
let idCounter = 0;

const Sparkline = ({ prices, color, height = 140 }) => {
    const [hoverIdx, setHoverIdx] = useState(null);
    const containerRef = useRef(null);
    const [cw, setCw] = useState(0);
    const gradIdRef = useRef(`spark-${++idCounter}`);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries => {
            const w = Math.round(entries[0]?.contentRect.width || 0);
            if (w > 0) setCw(w);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Filter out any entries with invalid close values
    const validPrices = prices?.filter(p => p && typeof p.close === 'number' && isFinite(p.close)) || [];
    const noData = validPrices.length < 2;
    const ready = !noData && cw > 0;

    const padY = 8;
    const padBottom = 22;
    const labelW = 52;
    const totalW = cw || 1;
    const chartW = totalW - labelW;
    const chartH = height - padY - padBottom;

    const fmt = (v) => v >= 1000 ? v.toFixed(0) : v >= 100 ? v.toFixed(1) : v.toFixed(2);
    const fmtDate = (d) => {
        if (!d) return '';
        try {
            const date = new Date(d);
            if (isNaN(date.getTime())) return '';
            const hasTime = String(d).includes(':');
            if (hasTime) return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } catch { return ''; }
    };
    const fmtXDate = (d) => {
        if (!d) return '';
        try {
            const date = new Date(d);
            if (isNaN(date.getTime())) return '';
            const hasTime = String(d).includes(':');
            if (hasTime) return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } catch { return ''; }
    };

    let closes = [], pointsArr = [], points = '', chartBottom = 0, areaPoints = '', ticks = [], xLabels = [], hp = null, tipPct = 0;

    if (ready) {
        closes = validPrices.map(p => p.close);
        const min = Math.min(...closes);
        const max = Math.max(...closes);
        const range = max - min || 1;
        pointsArr = closes.map((v, i) => ({
            x: (i / (closes.length - 1)) * chartW,
            y: padY + ((max - v) / range) * chartH,
        }));
        points = pointsArr.map(p => `${p.x},${p.y}`).join(' ');
        chartBottom = padY + chartH;
        areaPoints = `0,${chartBottom} ${points} ${chartW},${chartBottom}`;
        ticks = [0, 1, 2, 3].map(i => {
            const val = max - (range * i) / 3;
            const y = padY + (i / 3) * chartH;
            return { val, y };
        });
        const xLabelCount = 4;
        xLabels = Array.from({ length: xLabelCount }, (_, i) => {
            const idx = Math.round((i / (xLabelCount - 1)) * (closes.length - 1));
            return { x: pointsArr[idx].x, label: fmtXDate(validPrices[idx]?.date) };
        }).filter(l => l.label);
        hp = hoverIdx != null ? pointsArr[hoverIdx] : null;
        tipPct = hp ? (hp.x / totalW) * 100 : 0;
    }

    const handleMouseMove = (e) => {
        if (!ready) return;
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const mouseX = e.clientX - rect.left;
        if (mouseX < 0 || mouseX > chartW) { setHoverIdx(null); return; }
        const idx = Math.round((mouseX / chartW) * (closes.length - 1));
        setHoverIdx(Math.max(0, Math.min(closes.length - 1, idx)));
    };

    const gradId = gradIdRef.current;

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }} onMouseMove={handleMouseMove} onMouseLeave={() => setHoverIdx(null)}>
            {!ready ? (
                <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C7C7CC', fontSize: '0.8rem' }}>
                    {noData && cw > 0 ? 'No chart data' : ''}
                </div>
            ) : (
                <>
                    <svg viewBox={`0 0 ${totalW} ${height}`} style={{ width: '100%', height, display: 'block' }}>
                        <defs>
                            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={color} stopOpacity="0.18" />
                                <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                            </linearGradient>
                        </defs>
                        {ticks.map((t, i) => (
                            <g key={i}>
                                <line x1={0} y1={t.y} x2={chartW} y2={t.y} stroke="rgba(0,0,0,0.06)" strokeWidth="0.5" />
                                <text x={chartW + 6} y={t.y + 4} textAnchor="start" fill="#1D1D1F" fontSize="11" fontWeight="600" fontFamily="-apple-system, BlinkMacSystemFont, sans-serif">{fmt(t.val)}</text>
                            </g>
                        ))}
                        <polygon points={areaPoints} fill={`url(#${gradId})`} />
                        <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
                        <line x1={0} y1={chartBottom} x2={chartW} y2={chartBottom} stroke="rgba(0,0,0,0.08)" strokeWidth="0.5" />
                        {xLabels.map((l, i) => (
                            <text key={i} x={l.x} y={chartBottom + 15} textAnchor={i === 0 ? 'start' : i === xLabels.length - 1 ? 'end' : 'middle'} fill="#1D1D1F" fontSize="10" fontWeight="500" fontFamily="-apple-system, BlinkMacSystemFont, sans-serif">{l.label}</text>
                        ))}
                        {hp && <line x1={hp.x} y1={0} x2={hp.x} y2={chartBottom} stroke="rgba(0,0,0,0.15)" strokeWidth="1" strokeDasharray="3,2" />}
                    </svg>
                    {hp && (
                        <>
                            <div style={{ position: 'absolute', left: `${tipPct}%`, top: `${(hp.y / height) * 100}%`, width: 7, height: 7, borderRadius: '50%', backgroundColor: color, border: '1.5px solid #fff', transform: 'translate(-50%, -50%)', pointerEvents: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                            <div style={{ position: 'absolute', left: `${tipPct}%`, top: 2, transform: tipPct < 12 ? 'translateX(0)' : tipPct > 72 ? 'translateX(-100%)' : 'translateX(-50%)', backgroundColor: 'rgba(29,29,31,0.88)', backdropFilter: 'blur(8px)', color: '#fff', padding: '3px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 10, fontFamily: '-apple-system, sans-serif' }}>
                                {fmt(closes[hoverIdx])}
                                {validPrices[hoverIdx]?.date && <span style={{ opacity: 0.6, marginLeft: 4, fontSize: '0.6rem', fontWeight: 400 }}>{fmtDate(validPrices[hoverIdx].date)}</span>}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default Sparkline;
